/**
 * Whether the agent page opens with the sandbox (test) panel showing — closed unless this person
 * opened it last time (customer-portal review #96).
 *
 * The panel took a third of the width on every tab and squeezed History, Analytics and Channels,
 * so it now starts closed and the page remembers, per person on this browser, the last choice.
 * Keyed by the person's id so two people sharing a counter PC each get their own. A preference,
 * never configuration: storage that is blocked or empty simply means "closed".
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { useGateway } from "../../../api/provider.js";
import { authSessionQuery } from "../../../api/query.js";

const STORAGE_PREFIX = "wetalk.agent-sandbox-panel.";

export function sandboxPanelStorageKeyOf(personId: string): string {
  return `${STORAGE_PREFIX}${personId}`;
}

/** The stored choice: `true` only when this person last left the panel open. */
export function readSandboxPanelOpen(personId: string | null): boolean {
  if (personId === null) return false;
  try {
    return globalThis.localStorage.getItem(sandboxPanelStorageKeyOf(personId)) === "open";
  } catch {
    // Private browsing and blocked storage both throw. Not knowing means closed.
    return false;
  }
}

export function writeSandboxPanelOpen(personId: string | null, open: boolean): void {
  if (personId === null) return;
  try {
    globalThis.localStorage.setItem(sandboxPanelStorageKeyOf(personId), open ? "open" : "closed");
  } catch {
    // Remembering is a nicety; the panel itself still opens and closes.
  }
}

interface Choice {
  readonly personId: string | null;
  readonly open: boolean;
}

/**
 * `[open, setOpen]` for the agent page's sandbox panel, remembered for the signed-in person.
 *
 * The choice made on this page wins; before one is made, the stored choice of whoever is signed in
 * is read — so the person arriving after the first paint is followed without an effect.
 */
export function useSandboxPanelOpen(): readonly [boolean, (next: boolean | ((open: boolean) => boolean)) => void] {
  const gateway = useGateway();
  const authSession = useQuery(authSessionQuery(gateway));
  const personId = authSession.data?.person.personId ?? null;
  const [choice, setChoice] = useState<Choice | null>(null);
  const open = choice !== null && choice.personId === personId ? choice.open : readSandboxPanelOpen(personId);

  const setOpen = (next: boolean | ((current: boolean) => boolean)): void => {
    const value = typeof next === "function" ? next(open) : next;
    writeSandboxPanelOpen(personId, value);
    setChoice({ personId, open: value });
  };

  return [open, setOpen] as const;
}
